import { useState } from 'react';
import type { Stand } from '../lib/ansagen';
import { useAnsagen, type Spracheinstellung } from '../lib/sprache';

/**
 * Die Einstellung für gesprochene Ansagen.
 *
 * Steht in jedem Dashboard ganz unten – jede Person stellt für sich ein, ob und wie laut sie
 * etwas hört. Die Spielleitung entscheidet das **nicht** für alle; was bei der einen angenehm ist,
 * ist beim anderen zu laut.
 *
 * Sie spricht auch dann, wenn sie eingeklappt ist: Sichtbar sein muss nur die Einstellung.
 */
export function Ansagen({ stand }: { stand: Stand | null }) {
  const { einstellung, setzeEinstellung, umfrageEinstellung, setzeUmfrageEinstellung, stimmen, vorhanden, probe, umfrageProbe } = useAnsagen(stand);

  const [offen, setOffen] = useState(() => {
    try {
      return localStorage.getItem('bingo.ansagen.offen') === 'true';
    } catch {
      return false;
    }
  });

  function umschalten() {
    const nächster = !offen;
    setOffen(nächster);
    try {
      localStorage.setItem('bingo.ansagen.offen', String(nächster));
    } catch {
      // Auch ohne Browserspeicher bleibt der Bereich bedienbar.
    }
  }

  if (!vorhanden) {
    return (
      <section className="panel">
        <h2>Ansagen</h2>
        <p className="muted small">
          Dieser Browser bringt keine Sprachausgabe mit. In Chrome, Edge und Safari geht es.
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <button
        type="button"
        className="ghost"
        aria-expanded={offen}
        aria-controls="bingo-ansagen-einstellungen"
        onClick={umschalten}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: offen ? 16 : 0,
        }}
      >
        <strong>Ansagen & Lautstärke</strong>
        <span aria-hidden="true">{offen ? '▲' : '▼'}</span>
      </button>

      <div id="bingo-ansagen-einstellungen" hidden={!offen}>
      <h3>Bingo – Timer &amp; Bingo-Ansagen</h3>
      <SpracheBereich wert={einstellung} ändern={setzeEinstellung} stimmen={stimmen} probe={probe} />
      <h3>Challenge-Umfragen – Text-to-Speech</h3>
      <SpracheBereich wert={umfrageEinstellung} ändern={setzeUmfrageEinstellung} stimmen={stimmen} probe={umfrageProbe} />
      </div>
    </section>
  );
}

function SpracheBereich({ wert, ändern, stimmen, probe }: {
  wert: Spracheinstellung;
  ändern: (änderung: Partial<Spracheinstellung>) => void;
  stimmen: SpeechSynthesisVoice[];
  probe: () => void;
}) {
  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <label className="schalter">
        <input type="checkbox" checked={wert.an} onChange={(e) => ändern({ an: e.target.checked })} />
        <span>Ansagen aktivieren</span>
      </label>
      {wert.an && (
        <>
          <label>Lautstärke
            <input type="range" min={0} max={1} step={0.05} value={wert.lautstärke}
              onChange={(e) => ändern({ lautstärke: Number(e.target.value) })} />
          </label>
          <label>Sprechtempo
            <input type="range" min={0.7} max={1.5} step={0.05} value={wert.tempo}
              onChange={(e) => ändern({ tempo: Number(e.target.value) })} />
          </label>
          <label>Deutsche Stimme
            <select value={wert.stimme} onChange={(e) => ändern({ stimme: e.target.value })}>
              <option value="">Browser-Standard</option>
              {stimmen.map((stimme) => <option key={`${stimme.name}-${stimme.lang}`} value={stimme.name}>
                {stimme.name} ({stimme.lang})
              </option>)}
            </select>
          </label>
          <button type="button" className="ghost" onClick={probe}>Stimme testen</button>
        </>
      )}
    </div>
  );
}
